import React from 'react';
import ReactDOM from 'react-dom';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { ReactResizeDetector } from '~src/commonInfra/components/ReactResizeDetector';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';

import { ISizeWithTopOffset } from '../../../devicePreview/models/ISize';
import { IRange } from '../../models/IRange';

/**
 * Amount of extra items to fetch/render above and below the visible area, expressed as a fraction
 * of the height of the visible area
 */
export const SECTION_VERTICAL_BUFFER = 0.5;

export interface CardScrollerProps<T> {

  /**
   * A "sparse array" of the items to show in the scroll view.
   * The length of the array should be the total number of items in the list, and elements should
   * be undefined or null if they haven't been fetched. Fetched items should live at their respective
   * indexes, i.e. this array represents the full list of items, not just a particular slice.
   */
  readonly items: ReadonlyArray<T | undefined | null>;

  /*
    Content sort used to determine if the sort order has changed and we need to clear out the memoized
    objects and scroll to the top.
   */
  readonly contentSort: ReadonlyArray<Readonly<IOrderClause>>;

  /**
   * A function that requests missing items from the start to the end index, inclusive.
   * It is expected that, some time later, the missing items will be passed back in via the "items" prop.
   */
  readonly requestMissingSlice: (start: number, end: number) => void;

  /** Component for the fetched card */
  readonly renderFetchedCard: (props: { item: T, index: number, row: number }) => JSX.Element;

  /** Component for the unfetched card */
  readonly renderUnfetchedCard: () => JSX.Element;

  /** Each fetched item card must have a unique key; this function should provide those keys */
  readonly getKeyForItem: (item: T) => string;

  /** The height of each card. This is used to calculate the paging. */
  readonly itemHeightInPx: number;

  /** The width of each card. This is used to calculate the paging. */
  readonly itemWidthInPx: number;

  /**
   * The minimum number of pixels between cards. This number is static in the vertical dimension,
   * but may be larger in the horizontal dimension.
   */
  readonly itemSpacingInPx: number;

  /**
   * The maximum width of the entire card section. If the available space is wider than the max width,
   * the card scroller will be the max width and centered within the available space.
   */
  readonly cardScrollerMaxWidth?: number;

  /**
   * The horizontal padding on the items container, on either the left or the right (they are
   * assumed to be equal). This is used to calculate the paging.
   */
  readonly sectionHorizontalPaddingInPx: number;

  /**
   * Callback that gets invoked after the asynchronous layout update is complete.
   */
  readonly onLayoutUpdated?: () => void;

  /**
   * Allows accessible card reordering through the keyboard
   */
  readonly onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

export interface CardScrollerState {

  /** The measured size of the active section */
  readonly measuredSectionDimensions: ISizeWithTopOffset;
}

//This has to be an impure component as we want this to update everytime the cardview state/props changes
export class CardScroller<T> extends React.Component<CardScrollerProps<T>, CardScrollerState> {

  private element: Element;

  private memoizedGetCardDimensionsStyle = defaultMemoize(CardScrollerHelpers.getCardDimensionsStyle);

  private containerRef: React.RefObject<HTMLDivElement>;

  private constructor(props: CardScrollerProps<T>) {
    super(props);

    this.state = {
      measuredSectionDimensions: undefined
    };
    this.containerRef = React.createRef();
  }

  public componentDidMount() {
    this.element = ReactDOM.findDOMNode(this) as Element;
    this.updateLayoutAsynchronously();
  }

  public componentWillUnmount() {
    this.element = null;
  }

  public componentDidUpdate(prevProps: CardScrollerProps<T>, prevState: CardScrollerState) {
    const itemCountHasChanged = prevProps.items.length !== this.props.items.length;
    const sortHasChanged = prevProps.contentSort !== this.props.contentSort;
    if (itemCountHasChanged || sortHasChanged) {
      this.element.scrollTop = 0;
    }

    if (this.state.measuredSectionDimensions) {
      const { cardRange } = CardScrollerHelpers.getCardAndRowRangeForSection({
        sectionDimensions: this.state.measuredSectionDimensions,
        itemHeightInPx: this.props.itemHeightInPx,
        itemWidthInPx: this.props.itemWidthInPx,
        itemSpacingInPx: this.props.itemSpacingInPx,
      });

      // the saga takes care of snapping to whole page increments and deduping the same requests
      this.props.requestMissingSlice(cardRange.start, cardRange.end);

      if (this.state.measuredSectionDimensions !== prevState.measuredSectionDimensions) {
        this.props.onLayoutUpdated && this.props.onLayoutUpdated();
      }
    }
  }

  private handleScroll = () => {
    this.updateLayoutAsynchronously();
  };

  private handleResize = () => {
    this.updateLayoutAsynchronously();
  };

  // We must use defer because the element needs to have rendered on the screen before we measure it
  private updateLayoutAsynchronously = () => _.defer(() => {
    if (this.element) {
      let measuredSectionDimensions = CardScrollerHelpers.measureSectionDimensions({
        element: this.element,
        sectionHorizontalPadding: this.props.sectionHorizontalPaddingInPx,
        sectionVerticalBuffer: SECTION_VERTICAL_BUFFER,
        cardScrollerMaxWidth: this.props.cardScrollerMaxWidth,
      });

      this.setState({ measuredSectionDimensions });
    }
  });

  private static ScrollContainerStyle: React.CSSProperties = {
    height: '100%', // DO NOT ADD A WIDTH; IT HIDES THE SCROLL BAR
    overflowY: 'auto',
  };

  public render() {
    let totalHeightDivStyle: React.CSSProperties;
    let sectionStyle: React.CSSProperties = {
      padding: 0,
      margin: 0,
      listStyle: 'none',
    };
    let cardsInSection: JSX.Element[];

    const totalItemCount = this.props.items.length;

    if (this.state.measuredSectionDimensions) {
      const cardWidthWithSpacing = this.props.itemWidthInPx + this.props.itemSpacingInPx;
      const numberOfColumns = CardScrollerHelpers.calculateNumberOfColumns(this.state.measuredSectionDimensions, cardWidthWithSpacing);
      const totalRowCount = Math.ceil(totalItemCount / numberOfColumns);

      const itemHeightWithSpacing = this.props.itemHeightInPx + this.props.itemSpacingInPx;
      const sectionMarginBottom = this.props.itemSpacingInPx;
      const totalHeight = totalRowCount ? totalRowCount * itemHeightWithSpacing + sectionMarginBottom : 0;

      totalHeightDivStyle = {
        position: 'relative',
        height: totalHeight,
        maxWidth: this.props.cardScrollerMaxWidth,
        marginLeft: 'auto',
        marginRight: 'auto',
      };

      const { cardRange, rowRange } = CardScrollerHelpers.getCardAndRowRangeForSection({
          sectionDimensions: this.state.measuredSectionDimensions,
          itemHeightInPx: this.props.itemHeightInPx,
          itemWidthInPx: this.props.itemWidthInPx,
          itemSpacingInPx: this.props.itemSpacingInPx,
      });
      const sectionTopOffsetSnappedToRow = rowRange.start * itemHeightWithSpacing;

      sectionStyle = {
        ...sectionStyle,
        position: 'absolute',
        top: Math.max(sectionTopOffsetSnappedToRow, 0),
        left: 0,
        right: 0,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: (totalItemCount <= numberOfColumns) ? 'flex-start' : 'space-around',
        paddingLeft: this.props.sectionHorizontalPaddingInPx,
        paddingRight: this.props.sectionHorizontalPaddingInPx,
        marginBottom: sectionMarginBottom,
      };

      cardsInSection = this.makeCardElementsForSection(this.props.items, cardRange, numberOfColumns);
    }

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    return (
      <div style={this.props.items.length > 0 ? CardScroller.ScrollContainerStyle : {}}
           data-tb-test-id={ CardScrollerTestIds.CardView }
           data-tb-total-item-count={ totalItemCount }
           onScroll={this.handleScroll}
           onKeyDown={this.props.onKeyDown}
           ref={this.containerRef}>
        <div style={totalHeightDivStyle}>
          <ul style={sectionStyle}
                   data-tb-test-id={ CardScrollerTestIds.ActiveSection }>
            { cardsInSection }
          </ul>
        </div>
        <ReactResizeDetector targetRef={this.containerRef} handleWidth handleHeight onResize={this.handleResize} />
      </div>
    );
  }

  private makeCardElementsForSection(items: ReadonlyArray<T>, cardRange: IRange, numberOfColumns: number): JSX.Element[] {
    const totalItemCount = items.length;
    const fillerCardCount = CardScrollerHelpers.calculateFillerCardsNeeded({ actualItemCount: totalItemCount, cardsPerRow: numberOfColumns });

    // If section ends beyond the end of the item list, cut it off
    const boundedEndIndex = Math.min(cardRange.end, totalItemCount + fillerCardCount - 1);

    let cards: JSX.Element[] = [];
    for (let i = cardRange.start; i <= boundedEndIndex; i++) {
      cards.push(this.getCardAtIndex(i, items, numberOfColumns));
    }

    return cards;
  }

  private getCardAtIndex(index: number, items: ReadonlyArray<T>, numberOfColumns: number) {
    const cardDimensionsStyle = this.memoizedGetCardDimensionsStyle(
        this.props.itemHeightInPx, this.props.itemWidthInPx, this.props.itemSpacingInPx
    );

    const FetchedCardComponent = this.props.renderFetchedCard;
    const UnfetchedCardComponent = this.props.renderUnfetchedCard;

    const isFillerCard = index >= items.length;
    const isFetchedCard = !!items[index];

    if (isFillerCard) {
      return (
        <li style={cardDimensionsStyle}
             key={`filler-${index}`}
             aria-hidden={true}
        />
      );

    } else if (isFetchedCard) {
      const item = items[index];
      return (
        <li style={cardDimensionsStyle}
             key={this.props.getKeyForItem(item)}>
          <FetchedCardComponent item={item} index={index} row={Math.floor(index / numberOfColumns)}/>
        </li>
      );

    } else {
      return (
        <li style={cardDimensionsStyle}
             key={`unfetched-${index}`}>
          <UnfetchedCardComponent />
        </li>
      );
    }
  }
}

export namespace CardScrollerTestIds {
  export const CardView = 'tb-card-view';
  export const ActiveSection = 'tb-card-view-active-section';
}


export namespace CardScrollerHelpers {

  export function getCardDimensionsStyle(height: number, width: number, spacing: number): React.CSSProperties {
    return {
      height: height,
      width: width,
      marginTop: spacing,
      marginRight: spacing / 2,
      marginBottom: 0,
      marginLeft: spacing / 2,
      padding: 0,
    };
  }

  export interface MeasureSectionDimensionsParams {
    element: Element;
    sectionHorizontalPadding: number;
    sectionVerticalBuffer: number;
    cardScrollerMaxWidth?: number;
  }

  /**
   * Returns the measured size, in pixels, of the section to render. Does not trim any boundaries
   * (i.e. the top offset could be negative).
   */
  export function measureSectionDimensions(params: MeasureSectionDimensionsParams): ISizeWithTopOffset {
    const { element, sectionHorizontalPadding, sectionVerticalBuffer, cardScrollerMaxWidth } = params;

    // Because we don't allow horizontal overflow/scrolling, we can safely use scrollWidth, instead
    // of clientWidth. We must use clientHeight, however, because we do allow vertical scrolling
    // (we want only the height of what the user sees).
    const widthThatCanDisplayCards = Math.min(element.scrollWidth, cardScrollerMaxWidth || Infinity);
    const widthMinusPadding = widthThatCanDisplayCards - 2 * sectionHorizontalPadding;

    const visibleHeight = element.clientHeight;
    const bufferHeight = visibleHeight * sectionVerticalBuffer;
    const sectionHeight = visibleHeight + (2 * bufferHeight);

    const topOffset = element.scrollTop - bufferHeight;

    return {
      height: sectionHeight,
      width: widthMinusPadding,
      topOffset: topOffset,
    };
  }

  export function calculateNumberOfColumns(sectionDimensions: ISizeWithTopOffset, columnWidth: number): number {
    return Math.floor(Math.max(sectionDimensions.width / columnWidth, 1));
  }

  export function calculateFillerCardsNeeded(params: { actualItemCount: number, cardsPerRow: number }): number {
    const { actualItemCount, cardsPerRow } = params;
    const trailingItemCount = cardsPerRow > 0 ? actualItemCount % cardsPerRow : 0;
    return trailingItemCount > 0 ? cardsPerRow - trailingItemCount : 0;
  }


  export interface SectionRanges {
    cardRange: IRange;
    rowRange: IRange;
  }

  export interface GetCardAndRowRangeParams {
    sectionDimensions: ISizeWithTopOffset;
    itemHeightInPx: number;
    itemWidthInPx: number;
    itemSpacingInPx: number;
  }

  /**
   * Returns the indexes of the first and last card/row that should be in the section (the visible portion
   * of the list, plus a buffer above and below). If the section is at the beginning of the list where
   * the top buffer would go into negative indexes, they will get cut off at 0.
   */
  export function getCardAndRowRangeForSection(params: GetCardAndRowRangeParams): SectionRanges {
    const { sectionDimensions, itemHeightInPx, itemWidthInPx, itemSpacingInPx } = params;

    const cardHeight = itemHeightInPx + itemSpacingInPx;
    const cardWidth = itemWidthInPx + itemSpacingInPx;

    const numberOfColumns = calculateNumberOfColumns(sectionDimensions, cardWidth);

    const indexOfFirstRowInSection = Math.floor(sectionDimensions.topOffset / cardHeight);
    const indexOfFirstCardInSection = indexOfFirstRowInSection * numberOfColumns;

    const indexOfFirstRowPastEndOfSection = Math.ceil((sectionDimensions.topOffset + sectionDimensions.height) / cardHeight);
    const indexOfLastCardInSection = indexOfFirstRowPastEndOfSection * numberOfColumns - 1;

    return {
      cardRange: {
        start: Math.max(indexOfFirstCardInSection, 0),
        end: indexOfLastCardInSection
      },
      rowRange: {
        start: Math.max(indexOfFirstRowInSection, 0),
        end: indexOfFirstRowPastEndOfSection - 1
      }
    };
  }
}
